/**
 * ArticleIndexSectionEditor — binds ArticleIndexSettings to one section of the FlexibleLayout list.
 * Loads the folder / topic options through useArticleIndexOptions and writes the whole sections array
 * on every change (flexible-layout Rule 1: never a deep index path).
 */
import React, { useCallback } from 'react';
import type { ArticleIndexContent, LayoutSection } from '@segmently/schema-models/v2';
import { useArticleIndexOptions } from '../../../hooks/useArticleIndexOptions';
import { ArticleIndexSettings } from './ArticleIndexSettings';

export interface ArticleIndexSectionEditorProps {
  section: LayoutSection;
  sections: LayoutSection[];
  writeSections: (updated: LayoutSection[]) => void;
  locale: string;
  projectId?: string;
  disabled?: boolean;
}

export const ArticleIndexSectionEditor: React.FC<ArticleIndexSectionEditorProps> = ({
  section,
  sections,
  writeSections,
  locale,
  projectId,
  disabled,
}) => {
  const options = useArticleIndexOptions(projectId, locale);

  const handleChange = useCallback(
    (content: ArticleIndexContent) => {
      writeSections(sections.map((candidate) => (candidate.id === section.id ? { ...candidate, articleIndexContent: content } : candidate)));
    },
    [sections, section.id, writeSections],
  );

  return (
    <ArticleIndexSettings
      section={section}
      onChange={handleChange}
      folders={options.folders}
      topics={options.topics}
      locale={locale}
      loading={options.loading}
      disabled={disabled}
    />
  );
};
